import { type ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { createContext, ReactNode, useState } from 'react';

import { useAccountInfo } from 'src/components/AccountInfoProvider';
import { keepProperties } from 'src/helpers/utils';

import { ColorAndVariantValuesDictionary } from './colorConfiguration.types';
import { ColorConfigurationProvider } from './ColorConfigurationProvider';
import { SAVE_COLOR_CONFIGURATION } from './queries';

type ColorConfigurationLoaderContextValue =
  | undefined
  | {
      saveConfiguration: (_configuration: ColorAndVariantValuesDictionary) => Promise<void>;
      isSavingColorConfiguration: boolean;
      saveColorConfigurationError: ErrorLike | undefined;
      saveColorConfigurationData: {
        updateExtranetColorConfiguration: ColorAndVariantValuesDictionary;
      };
    };

type ColorConfigurationLoaderProviderProps = {
  children: ReactNode;
};

export const ColorConfigurationLoaderContext = createContext<ColorConfigurationLoaderContextValue>(undefined);

export function ColorConfigurationLoaderProvider(props: ColorConfigurationLoaderProviderProps) {
  const { extranetColorConfiguration, extranetCustomColor } = useAccountInfo();
  const [savedConfiguration, setSavedConfiguration] = useState(extranetColorConfiguration ?? extranetCustomColor);
  const [
    commitSaveColorConfiguration,
    { loading: isSavingColorConfiguration, error: saveColorConfigurationError, data: saveColorConfigurationData },
  ] = useMutation(SAVE_COLOR_CONFIGURATION);

  return (
    <ColorConfigurationLoaderContext.Provider
      value={{
        saveConfiguration,
        isSavingColorConfiguration,
        saveColorConfigurationError,
        saveColorConfigurationData: saveColorConfigurationData!,
      }}
    >
      <ColorConfigurationProvider configuration={savedConfiguration}>{props.children}</ColorConfigurationProvider>
    </ColorConfigurationLoaderContext.Provider>
  );

  /**
   * Save the given color configuration.
   * @param configuration Color configuration to save.
   */
  async function saveConfiguration(configuration: ColorAndVariantValuesDictionary) {
    const cleanConfiguration = keepProperties(configuration, ['value']) as ColorAndVariantValuesDictionary;

    const isConfigurationSaved = await commitSaveColorConfiguration({
      variables: {
        configuration: cleanConfiguration,
      },
    });

    if (isConfigurationSaved) {
      setSavedConfiguration(cleanConfiguration);
    }
  }
}
